import React, { useState, useMemo, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, TextField, TableSortLabel, Avatar, CircularProgress, InputAdornment } from '@mui/material';
import { Edit, Delete, Description, CalendarToday, AttachMoney, Category, Source, Repeat, InsertDriveFile, Search } from '@mui/icons-material';
import { designTokens } from '../theme/designTokens';
import { Income } from '../types/Income';
import { FormattedMessage, useIntl } from 'react-intl';
import IncomeForm from './IncomeForm';
import { getTokenDetails } from '../services/coingeckoService';
import { TableSkeleton } from './LoadingSkeleton';

interface IncomeListProps {
  income: Income[];
  onEditIncome: (updatedIncome: Income) => Promise<void>;
  onDeleteIncome: (id: string) => Promise<void>;
  categories: string[];
  onAddCategory: (category: string) => void;
  onEditCategory: (oldCategory: string, newCategory: string) => void;
}

type SortField = 'date' | 'description' | 'amount' | 'category' | 'source';
type SortOrder = 'asc' | 'desc';

const IncomeList: React.FC<IncomeListProps> = ({ income, onEditIncome, onDeleteIncome, categories, onAddCategory, onEditCategory }) => {
  const intl = useIntl();
  const [editingIncome, setEditingIncome] = useState<Income | null>(null);
  const [viewingReceipt, setViewingReceipt] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [tokenDetails, setTokenDetails] = useState<{ [key: string]: { logo: string, currentPrice: number } }>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTokenDetails = async () => {
      const tokens = [...new Set(income.map(entry => entry.token))];
      const details: { [key: string]: { logo: string, currentPrice: number } } = {};

      for (const token of tokens) {
        try {
          const tokenInfo = await getTokenDetails(token);
          if (tokenInfo) {
            details[token] = {
              logo: tokenInfo.image,
              currentPrice: tokenInfo.current_price
            };
          }
        } catch (error) {
          console.error(`Error fetching details for ${token}:`, error);
        }
      }

      setTokenDetails(details);
      setIsLoading(false);
    };

    fetchTokenDetails();
  }, [income]);

  const handleEditClick = (incomeItem: Income) => {
    setEditingIncome(incomeItem);
  };

  const handleEditClose = () => {
    setEditingIncome(null);
  };

  const handleEditSubmit = async (updatedIncome: Omit<Income, 'id' | 'userId'>): Promise<void> => {
    if (editingIncome) {
      await onEditIncome({ ...updatedIncome, id: editingIncome.id, userId: editingIncome.userId });
      setEditingIncome(null);
    }
  };

  const handleViewReceipt = (fileUrl: string | undefined) => {
    if (fileUrl) {
      setViewingReceipt(fileUrl);
    }
  };

  const handleCloseReceipt = () => {
    setViewingReceipt(null);
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedIncome = useMemo(() => {
    return income
      .filter(entry =>
        entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.source.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        const aValue = a[sortField as keyof Income] ?? '';
        const bValue = b[sortField as keyof Income] ?? '';
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
  }, [income, searchTerm, sortField, sortOrder]);

  if (isLoading) {
    return <TableSkeleton />;
  }

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: designTokens.borderRadius.lg,
          background: designTokens.colors.background.paper,
          border: designTokens.glassmorphism.border,
          boxShadow: designTokens.shadows.md,
          overflow: 'hidden',
          mt: 3
        }}
      >
        <Box sx={{ p: 3, borderBottom: `1px solid ${designTokens.colors.background.elevated}55` }}>
          <Typography variant="h5" sx={{ fontWeight: designTokens.typography.fontWeight.bold, mb: 2 }}>
            <FormattedMessage id="incomeList" />
          </Typography>
          <TextField
            fullWidth
            placeholder={intl.formatMessage({ id: 'search' })}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: designTokens.colors.text.secondary }} />
                </InputAdornment>
              ),
              sx: { borderRadius: designTokens.borderRadius.md }
            }}
          />
        </Box>
        <Table>
          <TableHead sx={{ bgcolor: `${designTokens.colors.background.elevated}33` }}>
            <TableRow>
              <TableCell sx={{ fontWeight: designTokens.typography.fontWeight.semibold, color: designTokens.colors.text.secondary }}>
                <TableSortLabel
                  active={sortField === 'date'}
                  direction={sortField === 'date' ? sortOrder : 'asc'}
                  onClick={() => handleSort('date')}
                >
                  <FormattedMessage id="date" />
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: designTokens.typography.fontWeight.semibold, color: designTokens.colors.text.secondary }}>
                <TableSortLabel
                  active={sortField === 'description'}
                  direction={sortField === 'description' ? sortOrder : 'asc'}
                  onClick={() => handleSort('description')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Description sx={{ mr: 1, fontSize: 18 }} />
                    <FormattedMessage id="description" />
                  </Box>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: designTokens.typography.fontWeight.semibold, color: designTokens.colors.text.secondary }}>
                <TableSortLabel
                  active={sortField === 'amount'}
                  direction={sortField === 'amount' ? sortOrder : 'asc'}
                  onClick={() => handleSort('amount')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachMoney sx={{ mr: 1, fontSize: 18 }} />
                    <FormattedMessage id="amount" />
                  </Box>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: designTokens.typography.fontWeight.semibold, color: designTokens.colors.text.secondary }}>
                <TableSortLabel
                  active={sortField === 'category'}
                  direction={sortField === 'category' ? sortOrder : 'asc'}
                  onClick={() => handleSort('category')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Category sx={{ mr: 1, fontSize: 18 }} />
                    <FormattedMessage id="category" />
                  </Box>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: designTokens.typography.fontWeight.semibold, color: designTokens.colors.text.secondary }}>
                <TableSortLabel
                  active={sortField === 'source'}
                  direction={sortField === 'source' ? sortOrder : 'asc'}
                  onClick={() => handleSort('source')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Source sx={{ mr: 1, fontSize: 18 }} />
                    <FormattedMessage id="source" />
                  </Box>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: designTokens.typography.fontWeight.semibold, color: designTokens.colors.text.secondary }}>
                <FormattedMessage id="actions" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedIncome.map((entry) => (
              <TableRow
                key={entry.id}
                sx={{
                  '&:hover': { bgcolor: `${designTokens.colors.background.elevated}22` },
                  transition: 'background-color 0.2s'
                }}
              >
                <TableCell sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                  {new Date(entry.date).toLocaleDateString()}
                </TableCell>
                <TableCell sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}33`, fontWeight: designTokens.typography.fontWeight.medium }}>
                  {entry.description}
                </TableCell>
                <TableCell sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {entry.token && tokenDetails[entry.token] && (
                      <Avatar src={tokenDetails[entry.token].logo} sx={{ width: 24, height: 24 }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: designTokens.typography.fontWeight.bold, color: designTokens.colors.primary.main }}>
                      €{entry.amount.toFixed(2)}
                    </Typography>
                    {entry.token && <Typography variant="caption" color="textSecondary">{entry.token}</Typography>}
                  </Box>
                </TableCell>
                <TableCell sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                  {entry.category}
                </TableCell>
                <TableCell sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                  {entry.source}
                </TableCell>
                <TableCell sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                  <IconButton
                    onClick={() => handleEditClick(entry)}
                    size="small"
                    sx={{
                      color: designTokens.colors.primary.main,
                      transition: `all ${designTokens.transitions.fast}`,
                      '&:hover': {
                        backgroundColor: `${designTokens.colors.primary.main}15`,
                        transform: 'translateY(-1px)',
                      }
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => onDeleteIncome(entry.id)}
                    size="small"
                    sx={{
                      color: designTokens.colors.error.main,
                      transition: `all ${designTokens.transitions.fast}`,
                      '&:hover': {
                        backgroundColor: `${designTokens.colors.error.main}15`,
                        transform: 'translateY(-1px)',
                      }
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!editingIncome}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: designTokens.borderRadius.lg,
            background: designTokens.colors.background.paper,
            border: designTokens.glassmorphism.border,
            boxShadow: designTokens.shadows.lg
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
          <FormattedMessage id="editIncome" />
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {editingIncome && (
            <IncomeForm
              onAddIncome={handleEditSubmit}
              categories={categories}
              onAddCategory={onAddCategory}
              onEditCategory={onEditCategory}
              initialIncome={editingIncome}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleEditClose}
            sx={{ textTransform: 'none', borderRadius: designTokens.borderRadius.md }}
          >
            <FormattedMessage id="cancel" />
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewingReceipt} onClose={handleCloseReceipt}>
        <DialogTitle><FormattedMessage id="viewReceipt" /></DialogTitle>
        <DialogContent>
          {viewingReceipt && (
            <img src={viewingReceipt} alt="Receipt" style={{ maxWidth: '100%', maxHeight: '80vh' }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReceipt}><FormattedMessage id="close" /></Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IncomeList;